/** Окно «Похожие» вкладки «Товар»: похожие на платья, которые клиент мерил (сетка примерки сделки
 * чата), — блоками «мерили → что ещё есть в салоне», коллаж до 9 фото и ссылка на страницу
 * `my.uni-renter.ru/p/<хэш>` со всеми похожими. «В поле ответа» кладёт коллаж файлом и текст со
 * ссылкой — отправляет сотрудник сам. Заготовку собирает фон `dress_tried`, когда сделка в «Думают»;
 * нет её или сетка сменилась — собирает по открытию окна. Спека `docs/specs/2026-10-26-fitting-similar-design.md`. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, RefreshCw, X } from "lucide-react";
import { useEffect } from "react";

import type { OnOffer } from "@/components/chat/SerialPhotos";
import { dt } from "@/components/timeline/Timeline";
import { apiFetch } from "@/lib/api";

type Serial = { serial: number; price: number; sale: boolean; city: string };
type Card = { model_id: number; photo: string; name: string; type: string; price: number | null; serials: Serial[] };
type Section = {
  base: { serial: number; model_id: number; photo: string; computed_date: string | null; error: string };
  count: number;
  cards: Card[];
};
export type Tried = {
  deal_id: number;
  url: string;
  city: string | null;
  text: string;
  collage: string | null;
  built_date: string;
  offered_date: string | null;
  similar: number;
  sections: Section[];
  other: { serial: number; photo: string }[];
};

const tried = (chatId: number, again = false) =>
  apiFetch<Tried>(`/api/chats/${chatId}/tried`, { method: "POST", body: JSON.stringify({ again }) });
const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;

async function collageFile(url: string, dealId: number): Promise<File> {
  const r = await fetch(url, { credentials: "same-origin" });
  if (!r.ok) throw new Error("Коллаж не загрузился");
  // Имя файла видит клиент.
  return new File([await r.blob()], `pohozhie-${dealId}.jpg`, { type: "image/jpeg" });
}

function Thumb({ c }: { c: Card }) {
  const s = c.serials[0];
  return (
    <li className="w-[92px] flex-none">
      <a href={c.photo} target="_blank" rel="noreferrer">
        <img src={c.photo} alt={c.name} loading="lazy" className="aspect-[3/4] w-full rounded-[8px] bg-black/5 object-cover" />
      </a>
      {s && (
        <div className="truncate text-[11.5px] leading-tight" title={c.name}>
          {s.serial} · {s.sale ? "продажа " : ""}
          {rub(s.price)}
        </div>
      )}
    </li>
  );
}

export function TriedDialog({ chatId, onClose, onOffer }: { chatId: number; onClose: () => void; onOffer: OnOffer }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["chat-tried", chatId], queryFn: () => tried(chatId), staleTime: 60_000, retry: false });
  const again = useMutation({
    mutationFn: () => tried(chatId, true),
    onSuccess: (d) => qc.setQueryData(["chat-tried", chatId], d),
  });
  const offer = useMutation({
    mutationFn: async (d: Tried) => {
      const files = d.collage ? [await collageFile(d.collage, d.deal_id)] : [];
      await apiFetch(`/api/chats/${chatId}/tried/offered`, { method: "POST" });
      return { text: d.text, files };
    },
    onSuccess: ({ text, files }) => {
      onOffer(text, files);
      void qc.invalidateQueries({ queryKey: ["chat-tried", chatId] });
      onClose();
    },
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const d = q.data;
  const err = (q.error ?? again.error ?? offer.error) as Error | null;
  const btn = "inline-flex items-center gap-1.5 rounded-[var(--radius-ui)] border border-border px-3 py-1.5 text-[13px] hover:bg-bg-subtle disabled:opacity-50";
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 sm:p-4" onClick={onClose}>
      <div
        className="flex w-full max-w-[1080px] flex-col overflow-hidden bg-bg text-fg sm:rounded-[16px]"
        onClick={(e) => e.stopPropagation()}
        data-annot="похожие по примерке"
      >
        <div className="flex flex-none flex-wrap items-center gap-3 border-b border-border px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-medium">Похожие на то, что мерили</div>
            {d && (
              <div className="text-[12px] text-fg-muted">
                Сделка {d.deal_id}
                {d.city ? ` · салон ${d.city}` : ""} · похожих в салоне {d.similar} · собрано {dt(d.built_date)}
                {d.offered_date ? ` · в поле ответа клали ${dt(d.offered_date)}` : ""}
              </div>
            )}
          </div>
          {d && (
            <a href={d.url} target="_blank" rel="noreferrer" className={btn} title="Страница клиента со всеми похожими">
              <ExternalLink size={14} /> Страница
            </a>
          )}
          <button
            type="button"
            className={btn}
            disabled={!d || again.isPending}
            onClick={() => again.mutate()}
            title="Пересчитать похожих у платьев сетки и собрать коллаж заново"
          >
            <RefreshCw size={14} className={again.isPending ? "animate-spin" : ""} /> Пересобрать
          </button>
          <button type="button" onClick={onClose} className="grid size-8 place-items-center rounded-full hover:bg-bg-subtle" aria-label="Закрыть">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {q.isPending && <div className="text-fg-muted">Ищем похожие на платья примерки…</div>}
          {err && <div className="mb-3 text-err">{err.message}</div>}
          {d && (
            <div className="flex flex-col gap-4 md:flex-row md:items-start">
              {d.collage ? (
                <img src={d.collage} alt="Коллаж похожих" className="w-full rounded-[10px] border border-border md:w-[42%] md:flex-none" />
              ) : (
                <div className="text-fg-muted md:w-[42%]">Похожих в салоне сейчас нет — коллажа нет.</div>
              )}
              <div className="flex min-w-0 flex-1 flex-col gap-3">
                {d.sections.map((s) => (
                  <section key={s.base.model_id} className="rounded-[10px] border border-border p-2" data-annot="похожие: мерили">
                    <div className="mb-2 flex items-center gap-2">
                      <img src={s.base.photo} alt="" className="h-[72px] w-[54px] flex-none rounded-[6px] bg-black/5 object-cover" />
                      <div className="min-w-0 text-[12.5px]">
                        <div className="font-medium">Мерили серию {s.base.serial}</div>
                        <div className="text-fg-muted">
                          похожих в салоне: {s.count}
                          {s.base.computed_date ? ` · посчитано ${dt(s.base.computed_date)}` : ""}
                          {s.base.error ? ` · ${s.base.error}` : ""}
                        </div>
                      </div>
                    </div>
                    {s.cards.length > 0 && (
                      <ul className="flex gap-1.5 overflow-x-auto pb-1">
                        {s.cards.map((c) => (
                          <Thumb key={c.model_id} c={c} />
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
                {d.other.length > 0 && (
                  <div className="text-[12px] text-fg-muted">
                    Без похожих (не платья: похожие ищем только по платьям): серии {d.other.map((o) => o.serial).join(", ")}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {d && (
          <div className="flex flex-none flex-wrap items-end gap-3 border-t border-border px-4 py-2.5">
            <div className="min-w-0 flex-1 whitespace-pre-wrap text-[12.5px] text-fg-muted">{d.text}</div>
            <button
              type="button"
              className="rounded-[var(--radius-ui)] bg-[var(--color-accent)] px-4 py-2 text-[13px] font-medium text-white disabled:opacity-50"
              disabled={offer.isPending || d.similar === 0}
              onClick={() => offer.mutate(d)}
            >
              {offer.isPending ? "Готовим…" : "В поле ответа"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
